import {
	Button,
	Card,
	ControlledInput,
	ControlledPhoneInput,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
} from "@dune/ui";
import "@dune/ui/globals.css";
import {
	NewBrokerAccountData,
	PositionInTheCompanyChoice,
	SirenReturn,
} from "@dune/types";
import { ChevronDown } from "lucide-react";
import { Controller } from "react-hook-form";
import { useCreateNewBrokerAccount } from "../../../api/mutations/societies.mutations";
import { useOriasAndSirenContext } from "../../../context/oriasAndSirenContext";
import {
	RegisterMainDetailsFormData,
	useRegisterMainDetailsForm,
} from "./useRegisterMainDetailsForm";

function buildHeadquartersAddress(sirenData: SirenReturn): string {
	return `${sirenData?.headOfficeAddress?.streetNumber} ${sirenData?.headOfficeAddress?.streetName}, ${sirenData?.headOfficeAddress?.postalCode}, ${sirenData?.headOfficeAddress?.city}`;
}

export default function RegisterMainDetails() {
	const { oriasData, sirenData } = useOriasAndSirenContext();
	const { mutate, isPending } = useCreateNewBrokerAccount();

	const { control, handleSubmit, getValues, errors } =
		useRegisterMainDetailsForm({
			oriasNumber: oriasData?.informationBase?.registrationNumber || "",
			siren: oriasData?.informationBase?.siren,
			denomination: oriasData?.informationBase?.denomination || "",
			headquartersAddress: sirenData ? buildHeadquartersAddress(sirenData) : "",
			lastname: "",
			firstname: "",
			email: "",
			landlinePhone: undefined,
			phone: "",
		});

	const onSubmit = (formData: RegisterMainDetailsFormData) => {
		const newBrokerAccountData: NewBrokerAccountData = {
			firstname: formData.firstname,
			lastname: formData.lastname,
			email: formData.email,
			mobilePhone: formData.phone,
			positionInTheCompany: formData.function,
			oriasNumber: formData.oriasNumber ?? "",
			streetNumber: sirenData?.headOfficeAddress?.streetNumber ?? "",
			streetName: sirenData?.headOfficeAddress?.streetName ?? "",
			city: sirenData?.headOfficeAddress?.city ?? "",
			postalCode: sirenData?.headOfficeAddress?.postalCode ?? "",
		};
		if (formData.landlinePhone && formData.landlinePhone.trim() !== "") {
			newBrokerAccountData.landlinePhone = formData.landlinePhone;
		}
		mutate(newBrokerAccountData);
	};

	if (!oriasData?.informationBase?.registrationNumber) {
		return (
			<div className="bg-background-blue min-h-screen flex items-center justify-center">
				<p className="text-white text-2xl">Numéro ORIAS inconnu</p>
			</div>
		);
	}

	return (
		<div className="bg-background-blue min-h-screen flex items-center justify-center py-8">
			<div className="absolute top-0 left-0 p-6">
				<img src="/dune_white_logo.png" alt="Dune Logo" className="w-32" />
			</div>
			<div className="text-center flex flex-col w-1/2 gap-8">
				<div className="text-white text-2xl text-center">
					{getValues("denomination")}
				</div>
				<div>
					<div className="text-white justify-self-start mb-4">
						Informations société
					</div>
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-center">
						<div className="flex flex-row gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<div>Numéro ORIAS</div>
								<ControlledInput
									control={control}
									disabled
									name="oriasNumber"
								/>
							</div>
							<div className="flex flex-col items-start gap-3 w-full">
								<div>SIREN</div>
								<ControlledInput control={control} disabled name="siren" />
							</div>
						</div>
						<div className="flex flex-col items-start gap-3">
							<div>Dénomination</div>
							<ControlledInput control={control} disabled name="denomination" />
						</div>
						<div className="flex flex-col items-start gap-3">
							<div>Adresse du siège social</div>
							<ControlledInput
								control={control}
								disabled
								name="headquartersAddress"
							/>
						</div>
					</Card>
				</div>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col gap-8"
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleSubmit(onSubmit)();
						}
					}}
				>
					<div>
						<div className="text-white justify-self-start">
							Informations utilisateur
						</div>
						<div className="text-white text-sm justify-self-start mb-4">
							Complétez les informations relatives à votre profil utilisateur
						</div>

						<Card className="p-6 flex flex-col gap-4 overflow-auto text-center">
							<div className="flex flex-row gap-4">
								<div className="flex flex-col items-start gap-3 w-full">
									<div>Nom*</div>
									<ControlledInput
										control={control}
										name="lastname"
										error={errors.lastname?.message?.toString()}
									/>
								</div>
								<div className="flex flex-col items-start gap-3 w-full">
									<div>Prénom*</div>
									<ControlledInput
										control={control}
										name="firstname"
										error={errors.firstname?.message?.toString()}
									/>
								</div>
							</div>
							<div className="flex flex-row gap-4">
								<div className="flex flex-col items-start gap-3 w-full">
									<div>Email*</div>
									<ControlledInput
										control={control}
										name="email"
										error={errors.email?.message?.toString()}
									/>
								</div>
								<div className="flex flex-col items-start gap-3 w-full">
									<div>Fonction dans l'entreprise*</div>

									<Controller
										name="function"
										control={control}
										render={({ field }) => (
											<div className="relative w-full">
												<Input
													type="text"
													placeholder=""
													readOnly
													className="w-full"
													iconOrText={<ChevronDown />}
													iconOrTextPosition="right"
													value={
														field.value
															? PositionInTheCompanyChoice[field.value]
															: undefined
													}
												/>
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<div className="absolute inset-0 cursor-pointer" />
													</DropdownMenuTrigger>

													<DropdownMenuContent className="min-w-[300px]">
														{Object.entries(PositionInTheCompanyChoice).map(
															([key, value]) => (
																<DropdownMenuItem
																	key={key}
																	onClick={() => field.onChange(key)}
																>
																	{value}
																</DropdownMenuItem>
															),
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										)}
									/>
									{errors.function && (
										<span className="text-red-500 text-sm">
											{errors.function.message?.toString()}
										</span>
									)}
								</div>
							</div>
							<div className="flex flex-row gap-4">
								<div className="flex flex-col items-start gap-3 w-full">
									<div>Téléphone fixe</div>
									<ControlledPhoneInput
										control={control}
										name="landlinePhone"
										placeholder="Ex : +33 1 23 45 67 89"
										defaultCountry="FR"
									/>

									{errors.landlinePhone && (
										<span className="text-red-500 text-sm">
											{errors.landlinePhone.message?.toString()}
										</span>
									)}
								</div>
								<div className="flex flex-col items-start gap-3 w-full">
									<div>Mobile*</div>
									<ControlledPhoneInput
										control={control}
										name="phone"
										placeholder="Ex : +33 6 12 34 56 78"
										defaultCountry="FR"
									/>
									{errors.phone && (
										<span className="text-red-500 text-sm">
											{errors.phone.message?.toString()}
										</span>
									)}
								</div>
							</div>
						</Card>
					</div>
					<div>
						<Button
							variant="outline"
							type="submit"
							disabled={isPending}
							isLoading={isPending}
							className="h-11"
						>
							Devenir partenaire
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
